/**
 * Blog Post 22: Mortgage Amortization Explained for Hong Kong Homeowners
 * 香港按揭攤還解析：每月供款如何償還本金與利息
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

export default function BlogPost22() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';
    const slug = '/blog/hong-kong-mortgage-amortization-explained';

    useEffect(() => {
        trackPageView(slug, 'Blog Post 22');
        document.title = isEnglish
            ? 'Mortgage Amortization Explained: How Your Hong Kong Home Loan Works | PayWise365'
            : '香港按揭攤還解析：每月供款如何償還本金與利息 | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? 'Mortgage Amortization Explained: How Your Hong Kong Home Loan Works'
        : '香港按揭攤還解析：每月供款如何償還本金與利息';
    const seoDescription = isEnglish
        ? 'Understand how mortgage payments are split between interest and principal, why the balance falls slowly at first, and how rate or term changes affect your loan.'
        : '了解每月按揭供款如何分配至利息與本金、為何初期本金下降較慢，以及利率或年期變動如何影響貸款。';

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={isEnglish
                    ? 'mortgage amortization Hong Kong, mortgage principal and interest, monthly mortgage payment, loan amortization schedule, 按揭攤還'
                    : '按揭攤還, 按揭本金, 按揭利息, 每月供款, 還款表, 香港按揭'}
                canonicalUrl={`https://www.paywise365.com${slug}`}
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-10-06T00:00:00+08:00"
                modifiedDate="2026-10-06T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'Mortgage Guide' : '按揭指南'}
                tags={isEnglish
                    ? ['Mortgage', 'Amortization', 'Principal and Interest', 'Hong Kong Home Loan']
                    : ['按揭', '攤還', '本金與利息', '香港置業']}
            />
            <Box sx={{ bgcolor: '#155e63', color: 'white', py: { xs: 5, md: 7 } }}>
                <Container maxWidth="md">
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.85 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>{isEnglish ? 'Home' : '首頁'}</a>
                        {' / '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>{isEnglish ? 'Blog' : '資訊'}</a>
                        {' / '}
                        {isEnglish ? 'Mortgage Guide' : '按揭指南'}
                    </Typography>
                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? 'Mortgage Amortization, Explained'
                            : '一文看懂按揭攤還'}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.92 }}>
                        {isEnglish
                            ? 'See where each monthly payment goes, and what really moves your loan balance.'
                            : '看清每月供款的去向，以及真正影響貸款結餘的因素。'}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box component="article" sx={{ color: '#334155', lineHeight: 1.85, '& h2': { mt: 4, mb: 1.5, color: '#123b40', fontWeight: 700 }, '& p': { mb: 2 } }}>
                    {isEnglish ? (
                        <>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                Mortgage Guide · Published 6 October 2026 · 6 min read
                            </Typography>
                            <Typography>
                                A mortgage payment can look like one fixed monthly figure, but it is doing two different jobs: paying the lender interest and reducing the amount you borrowed. The schedule that tracks this split over time is called an <strong>amortization schedule</strong>. Understanding it makes your statement easier to read and helps you compare repayment choices with more confidence.
                            </Typography>

                            <Typography variant="h5">How a monthly payment is divided</Typography>
                            <Typography>
                                For a typical repayment mortgage, the lender calculates a regular payment using the loan balance, interest rate and remaining term. Each period, interest is calculated on the outstanding balance. The rest of that payment reduces principal:
                            </Typography>
                            <Box sx={{ bgcolor: '#f1f5f4', borderLeft: '4px solid #0f766e', p: 2.5, my: 2, fontFamily: 'monospace' }}>
                                <div>Interest for the period = outstanding balance × periodic interest rate</div>
                                <div>Principal repaid = monthly payment − interest for the period</div>
                            </Box>
                            <Typography>
                                As principal falls, the next interest charge is generally calculated on a smaller balance. If the rate and payment stay unchanged, more of each later payment can therefore go toward principal. Early payments often feel slow because the balance is largest at the beginning.
                            </Typography>

                            <Typography variant="h5">A simplified Hong Kong example</Typography>
                            <Typography>
                                Consider a hypothetical HK$4,000,000 repayment mortgage over 25 years at a constant annual rate of 3.5%, with monthly payments. The payment would be around HK$20,000 a month before fees or other charges. In the first month, interest alone would be about HK$11,667; only the remainder of that payment would reduce principal.
                            </Typography>
                            <Typography>
                                This is an illustration, not a bank quotation. It assumes a fixed rate for the full term and a standard monthly repayment calculation. Hong Kong mortgages may use HIBOR- or Prime-linked rates, caps, introductory offers, different day-count conventions or other terms. If the rate changes, the payment, repayment period or both may change too. Ask your lender how it recalculates your specific loan.
                            </Typography>

                            <Typography variant="h5">Why the balance may not match your intuition</Typography>
                            <Typography>
                                The original loan amount is not the same as the remaining principal, and the amount you have paid so far is not all principal. A substantial part of early payments can be interest. Your statement may also include fees, insurance, an offset arrangement, or transactions posted on dates that differ from your payment date. Check the statement’s outstanding-principal figure and transaction details rather than estimating the balance from total payments alone.
                            </Typography>

                            <Typography variant="h5">What changes the amortization path?</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>Interest rate:</strong> A higher rate usually means more interest is due each period. Depending on the loan terms, the bank may adjust the payment or extend the effective payoff time.</li>
                                    <li><strong>Loan term:</strong> A longer term generally lowers the required monthly payment but can increase total interest if the rate and other assumptions stay the same.</li>
                                    <li><strong>Extra repayments:</strong> An eligible principal repayment can reduce the balance on which future interest is charged. Check lock-in periods, fees, minimum amounts and whether the bank shortens the term or recalculates the payment.</li>
                                    <li><strong>Payment timing and product features:</strong> Offset balances, payment dates and the lender’s calculation method can affect the interest shown on your statement.</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">How to read your repayment schedule</Typography>
                            <Typography component="div">
                                <ol>
                                    <li>Confirm the opening balance and the rate applied for that period.</li>
                                    <li>Separate the interest charge from principal repaid.</li>
                                    <li>Check that the closing balance reflects the payment and any additional transactions.</li>
                                    <li>Compare the next period’s opening balance with the previous closing balance.</li>
                                    <li>When comparing offers, look at total interest and fees under the same rate and term assumptions, not just the headline monthly payment.</li>
                                </ol>
                            </Typography>

                            <Typography variant="h5">Put your own numbers to work</Typography>
                            <Typography>
                                Every mortgage has its own rate structure and conditions. Use the <a href="/calculators/house-loan" style={{ color: '#0f766e' }}>house loan calculator</a> to estimate a regular repayment, then compare it with your lender’s illustration. For a possible lump-sum repayment, the <a href="/calculators/partial-repayment" style={{ color: '#0f766e' }}>partial repayment calculator</a> can help you explore scenarios. Treat estimates as planning tools, and confirm the actual impact, charges and revised schedule with your bank before acting.
                            </Typography>
                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #dbe4e2', fontSize: '0.9rem' }} color="text.secondary">
                                This article is for general educational purposes and is not personal financial or mortgage advice. Rates, fees and loan terms vary by lender and can change.
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                按揭指南 · 發佈日期：2026年10月6日 · 閱讀時間：6分鐘
                            </Typography>
                            <Typography>
                                每月按揭供款看似一個固定數字，其實同時做兩件事：支付利息，以及償還借入的本金。記錄這個分配如何隨時間改變的表格，稱為<strong>按揭攤還表</strong>。了解背後原理，有助你看懂月結單，亦能更有根據地比較不同還款方案。
                            </Typography>

                            <Typography variant="h5">每月供款如何分配？</Typography>
                            <Typography>
                                一般按揭還款額會按貸款結餘、利率及剩餘年期計算。每個計息期先根據未償還結餘計算利息，供款扣除利息後的部分才用來償還本金：
                            </Typography>
                            <Box sx={{ bgcolor: '#f1f5f4', borderLeft: '4px solid #0f766e', p: 2.5, my: 2, fontFamily: 'monospace' }}>
                                <div>本期利息 = 未償還結餘 × 該期利率</div>
                                <div>償還本金 = 每月供款 − 本期利息</div>
                            </Box>
                            <Typography>
                                本金逐步減少後，下一期一般會以較低結餘計算利息。在利率及供款額不變的情況下，後期供款中用來償還本金的比例通常會增加。初期本金下降較慢，原因是當時貸款結餘最大。
                            </Typography>

                            <Typography variant="h5">香港按揭簡化例子</Typography>
                            <Typography>
                                假設一筆港幣400萬元的按揭，還款期25年，年利率全期維持3.5%，並按月供款。未計費用或其他收費，每月供款約為港幣20,000元。首月利息約為港幣11,667元，該月供款餘額才會用來減少本金。
                            </Typography>
                            <Typography>
                                這只是示例，並非銀行報價；假設全期利率固定，並採用一般按月還款計算。香港按揭可能採用與H按或P按掛鈎的利率、封頂利率、優惠期、不同計息日數或其他條款。利率變動後，銀行亦可能調整供款額、還款時間或兩者。應向貸款銀行確認你的按揭如何重新計算。
                            </Typography>

                            <Typography variant="h5">為何結餘未必符合直覺？</Typography>
                            <Typography>
                                原始貸款額不等於目前尚欠本金，而已繳供款亦不代表全部用來還本金。還款初期，供款中可能有相當部分是利息。月結單亦可能列出費用、保險、存款掛鈎安排，或因入帳日期與供款日期不同而出現差異。不要單憑累計供款估算結餘，應查看月結單上的未償還本金及交易紀錄。
                            </Typography>

                            <Typography variant="h5">哪些因素會改變還款進度？</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>利率：</strong>利率上升通常會增加每期利息。銀行會否調高供款或延長實際還款時間，則視乎貸款條款。</li>
                                    <li><strong>還款年期：</strong>在其他假設相同下，年期較長通常可降低每月最低供款，但總利息可能較高。</li>
                                    <li><strong>額外還款：</strong>符合條件的額外本金還款可降低日後計息的結餘。先查清罰息期、手續費、最低金額，以及銀行會縮短年期還是重新計算供款。</li>
                                    <li><strong>供款日期及產品安排：</strong>存款掛鈎結餘、供款日期及銀行的計息方法，都可能影響月結單上的利息。</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">閱讀還款表的簡單步驟</Typography>
                            <Typography component="div">
                                <ol>
                                    <li>核對期初結餘及該期適用利率。</li>
                                    <li>分辨當期利息與已償還本金。</li>
                                    <li>確認期末結餘已反映供款及其他交易。</li>
                                    <li>比較下一期的期初結餘與上一期的期末結餘。</li>
                                    <li>比較按揭方案時，應在相同利率及年期假設下比較總利息和費用，不要只看每月供款。</li>
                                </ol>
                            </Typography>

                            <Typography variant="h5">代入自己的數字</Typography>
                            <Typography>
                                每筆按揭的利率結構及條款都不同。你可使用<a href="/calculators/house-loan" style={{ color: '#0f766e' }}>房屋貸款計算機</a>估算定期供款，再與銀行提供的試算結果比較。如考慮一次過額外還款，可使用<a href="/calculators/partial-repayment" style={{ color: '#0f766e' }}>部分還款計算機</a>比較情境。計算結果只適合作規劃參考，作出決定前請向銀行確認實際影響、收費及更新後的還款表。
                            </Typography>
                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #dbe4e2', fontSize: '0.9rem' }} color="text.secondary">
                                本文只作一般教育用途，並非個人財務或按揭建議。利率、費用及貸款條款因銀行而異，亦可能有所變動。
                            </Typography>
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
}